import clsx from 'clsx'
import { RepoMap, WorkflowData, WorkflowRunConclusion } from '../types'
import { StatusIcon } from './utils/workflow-status'
import { timeSinceRunFinishedInBranch } from './utils/workflow-utils'
import * as icon from 'react-icons/go'
import { differenceInDays } from 'date-fns'
import { useState } from 'react'

type WorkflowDataWithRepoName = WorkflowData & { repoName: string }

const IGNORE_WORKFLOWS_WITH_NAME = ['Dependabot Updates']
const IGNORE_RUNS_OLDER_THAN_DAYS = 30

function getFailedWorkflows(
  repoWorkflows: RepoMap,
  isTimeFilterDisabled: boolean,
): WorkflowDataWithRepoName[] {
  const workflows = Object.values(repoWorkflows).flatMap((x) =>
    Object.values(x.workflows).map((workflowData) => ({
      ...workflowData,
      repoName: x.name,
    })),
  )

  return workflows
    .filter(
      (workflow) =>
        workflow.latestRun && workflow.latestRun.conclusion != 'success',
    )
    .filter((workflow) => !IGNORE_WORKFLOWS_WITH_NAME.includes(workflow.name))
    .filter((workflow) => {
      if (isTimeFilterDisabled) {
        return true
      }

      return (
        differenceInDays(new Date(), new Date(workflow.latestRun.finished_at)) <
        IGNORE_RUNS_OLDER_THAN_DAYS
      )
    })
    .toSorted(
      (a, b) =>
        new Date(b.latestRun.finished_at).getTime() -
        new Date(a.latestRun.finished_at).getTime(),
    )
}

function NoWorkflowRunsFound() {
  return (
    <div className="flex p-4">
      <span>No workflow runs found!</span>
      <span className="flex items-center ml-2">
        <icon.GoSmiley />
      </span>
    </div>
  )
}

function WorkflowResult({ workflow }: { workflow: WorkflowDataWithRepoName }) {
  const { latestRun, repoName, name } = workflow
  return (
    <div className="flex w-full pl-5 px-4 py-2 border-b last:border-0">
      <span className="w-sm">
        <a
          className="text-sm hover:bg-blue-100"
          href={latestRun.html_url}
          target="_blank"
          rel="noreferrer"
        >
          {repoName}
        </a>
      </span>
      <span className="flex-grow">
        <a
          className="text-sm hover:bg-blue-100"
          href={latestRun.html_url}
          target="_blank"
          rel="noreferrer"
        >
          {name}
        </a>
      </span>

      <span className="uppercase">
        {timeSinceRunFinishedInBranch(latestRun)}
      </span>
      <div className="flex items-center ml-2">
        <StatusIcon conclusion={latestRun?.conclusion as WorkflowRunConclusion}
        />
      </div>
    </div>
  )
}

export function Unstables({ repoWorkflows }: { repoWorkflows: RepoMap }) {
  const [isTimeFilterDisabled, disableTimeFilter] = useState(false)
  const workflowRuns = getFailedWorkflows(repoWorkflows, isTimeFilterDisabled)

  return (
    <div
      className={clsx(
        'border',
        'rounded overflow-hidden',
        'shadow-sm',
        'flex items-start flex-col',
        'border-b',
        'bg-neutral-100',
        'mt-5',
      )}
    >
      <div className="flex items-center text-lg font-mono border-b w-full pl-5 p-4">
        <span className="flex-grow">
          Unstable workflows
          <span
            className={`cursor-pointer pl-2 ${clsx({ 'line-through': isTimeFilterDisabled })}`}
            onClick={() => disableTimeFilter(!isTimeFilterDisabled)}
          >
            (last {IGNORE_RUNS_OLDER_THAN_DAYS} days)
          </span>
        </span>
        <div className="text-sm">
          <span>
            {`Ignores workflows with name: ${IGNORE_WORKFLOWS_WITH_NAME.join(', ')}`}
          </span>
        </div>
      </div>

      {workflowRuns.length === 0 && <NoWorkflowRunsFound />}

      {workflowRuns.map((workflow) => (
        <WorkflowResult key={workflow.html_url} workflow={workflow} />
      ))}
    </div>
  )
}
